import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams } from 'react-router-dom';
import { ContentTemplate, HeaderTemplate, Progress, Stepper, TitleHeader } from 'web-library';
import { Theme as AugmentedTheme } from '@mui/material/styles';

import { makeStyles } from 'tss-react/mui';
import VacancyExpiredModal from '../VacancyExpiredModal';
import NonNationalExamTypeUuidDisplay from '../NonNationalExamTypeUuidDisplay';
import ExamSchoolingDetailsForm from '../../../../modules/examRegistration/components/nonNationalExam/ExamSchoolingDetailsForm';
import { candidateService } from '../../../../api';
import {
  GetNonNationalExamSchoolingDetailsResponse,
  useGetNonNationalExamSchoolingDetails,
} from '../../../../api-refactored/non-national-exam-registration/useGetNonNationalExamSchoolingDetails';
import { AdmissionExamLevelResponse, IOptionType } from '../../../../api/candidate/exam/model';
import { useGetCountry } from '../../../../api-refactored/non-national-exam-registration/useGetCountry';
import { useGetSiblingExamLevel } from '../../../../api-refactored/non-national-exam-registration/useGetSiblingExamLevel';
import {
  NonNationalApplicationStatusResponse,
  useGetAdmissionLevel,
} from '../../../../api-refactored/non-national-exam-registration/useGetAdmissionLevel';

const useStyles = makeStyles()((theme) =>
({
  stepper: {
    position: 'relative',
    [theme.breakpoints.up('xl')]: {
      left: '-10%',
    },
    [theme.breakpoints.between('lg', 'xl')]: {
      left: '-10%',
    },
    [theme.breakpoints.between('md', 'xl')]: {
      left: '-2.5%',
    },
    [theme.breakpoints.between('sm', 'lg')]: {
      left: '-2.5%',
    },
    [theme.breakpoints.down('md')]: {
      left: '-3%',
    },
  },
}));

interface ExamSchoolingDetailsParams {
  uuid: string;
}

interface ExamSchoolingDetailsPageProps {
  routeSteps: { label: string }[];
  isOpsAdmin?: boolean;
}

export interface ExamSchoolingDetailsPageLocation {
  examType: string;
  examTitle: string;
  levelSeekingAdmission: IOptionType;
  isSupplementary: boolean;
}

const ExamSchoolingDetails = ({
  routeSteps,
  isOpsAdmin = false,
}: ExamSchoolingDetailsPageProps) => {
  const location = useLocation<ExamSchoolingDetailsPageLocation>();
  const { uuid } = useParams<ExamSchoolingDetailsParams>();
  const { state } = location;
  const { t } = useTranslation();
  const { classes } = useStyles();
  const examTitle =
    t('exam->>RegisterExam.RegisterForAnExam') +
    (state?.isSupplementary ? ` - ${t('exam->>Instructions.Supplementary')}` : '');
  const [isSuccessSiblingExamLevel, setIsSuccessSiblingExamLevel] = useState<boolean>(false);
  const [
    examSchoolingDetailsData,
    setExamSchoolingDetailsData,
  ] = useState<GetNonNationalExamSchoolingDetailsResponse>();
  const [isSuccess, setIsSuccess] = useState<boolean>(false);
  const [
    admissionLevelData,
    setAdmissionLevelData,
  ] = useState<NonNationalApplicationStatusResponse>();
  const [examTypesData, setExamTypesData] = useState<AdmissionExamLevelResponse>();

  const { data: ceqData } = candidateService.exam.candidate.useGetCEQType({
    onSuccess: () => {
      setIsSuccess(true);
    },
  });
  const { data: birthCountryData } = useGetCountry();

  const { data: siblingExamLevelData } = useGetSiblingExamLevel({
    onSuccess: () => {
      setIsSuccessSiblingExamLevel(true);
    },
  });

  useGetNonNationalExamSchoolingDetails({
    uuid,
    onSuccess: (res) => {
      setExamSchoolingDetailsData(res);
    },
  });

  candidateService.exam.candidate.useGetAdmissionExamLevel(state.examType, {
    onSuccess: (res) => {
      setExamTypesData(res);
    },
  });

  useGetAdmissionLevel({
    uuid,
    onSuccess: (res) => {
      setAdmissionLevelData(res);
    },
  });

  return (
    <>
      <TitleHeader title={isOpsAdmin ? t('exam->>NonNationalExam.ReviewApplication') : examTitle} />
      <HeaderTemplate>
        <NonNationalExamTypeUuidDisplay nonExamType={state?.examTitle || '-'} enableApi={false} />
        <Stepper className={classes.stepper} activeStep={1} steps={routeSteps} />
      </HeaderTemplate>
      {isSuccess &&
        examSchoolingDetailsData &&
        birthCountryData &&
        admissionLevelData &&
        isSuccessSiblingExamLevel &&
        examTypesData ? (
        <ExamSchoolingDetailsForm
          ceqData={ceqData}
          examSchoolingDetailsData={examSchoolingDetailsData}
          birthCountryData={birthCountryData}
          admissionLevelData={admissionLevelData}
          siblingExamLevelData={siblingExamLevelData}
          examTypesData={examTypesData}
          isOpsAdmin={isOpsAdmin}
        />
      ) : (
        <ContentTemplate transparent continuous>
          <Progress color="secondary" />
        </ContentTemplate>
      )}
      {!isOpsAdmin && <VacancyExpiredModal uuid={uuid} />}
    </>
  );
};

export default ExamSchoolingDetails;
